/* Collection-specific mobile layout enhancement. No product-card rewriting,
 * duplicate product fetches, or changes to the theme's desktop .js-btn-view code.
 */
(() => {
  'use strict';
  const tag = 'coral-collection-view';
  if (customElements.get(tag)) return;

  class CoralCollectionView extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.section = this.closest('.shopify-section');
      this.container = this.section?.querySelector('#ProductGridContainer');
      if (!this.container) return;
      this.initialized = true;
      this.events = new AbortController();
      this.storageKey = `coral:mobile-grid:${this.dataset.collectionKey || this.dataset.sectionId}`;
      this.columns = '2';
      try {
        const saved = sessionStorage.getItem(this.storageKey);
        if (saved === '1' || saved === '2') this.columns = saved;
      } catch (_) { /* Storage may be blocked in a private browsing context. */ }

      const drawer = this.section.querySelector('#main-collection-filters menu-drawer');
      this.moving = true;
      if (drawer?.parentElement) {
        this.toolbarRow = drawer.parentElement;
        this.toolbarRow.classList.add('cs-collection-native-row');
        this.toolbarRow.appendChild(this);
      } else {
        // Still offer the grid view when filtering and sorting are disabled.
        this.container.before(this);
      }
      this.moving = false;
      this.removeAttribute('hidden');
      const options = { signal: this.events.signal };
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-cs-grid-view]');
        if (!button || !this.contains(button)) return;
        this.setColumns(button.dataset.csGridView, true);
      }, options);
      document.addEventListener('coral:facets-complete', () => this.scheduleCount(), options);
      window.addEventListener('pageshow', () => {
        this.setColumns(this.columns);
        this.scheduleCount();
      }, options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (String(event.detail?.sectionId) === this.dataset.sectionId) this.remove();
      }, options);

      // ProductGridContainer stays in place; native facets replace its contents.
      // Ajaxinate appends products to the same grid. Observe both without owning
      // either implementation or overriding any app/product initialization.
      this.observer = new MutationObserver(() => this.scheduleCount());
      this.observer.observe(this.container, { childList: true, subtree: true });
      this.setColumns(this.columns);
      this.updateCount();
    }

    disconnectedCallback() {
      if (this.moving) return;
      this.events?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.countFrame);
      this.toolbarRow?.classList.remove('cs-collection-native-row');
      this.container?.removeAttribute('data-cs-mobile-columns');
      this.initialized = false;
    }

    setColumns(columns, persist = false) {
      if (columns !== '1' && columns !== '2') return;
      this.columns = columns;
      // A single data attribute, outside the replaced AJAX HTML, controls mobile
      // widths. Existing classes, event listeners and desktop layout stay intact.
      this.container.dataset.csMobileColumns = columns;
      this.querySelectorAll('[data-cs-grid-view]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.csGridView === columns));
      });
      if (persist) {
        try { sessionStorage.setItem(this.storageKey, columns); } catch (_) {}
      }
      this.scheduleCount();
    }

    scheduleCount() {
      cancelAnimationFrame(this.countFrame);
      this.countFrame = requestAnimationFrame(() => this.updateCount());
    }

    updateCount() {
      const summary = this.container.querySelector('[data-cs-collection-summary]');
      if (!summary) return;
      const grid = this.container.querySelector('#product-grid');
      const displayed = grid ? [...grid.children].filter((item) => item.matches('.collection-grid-item')).length : 0;
      const total = Number(summary.dataset.csMatchingTotal);
      if (!Number.isFinite(total)) return;
      let format;
      try { format = new Intl.NumberFormat(document.documentElement.lang || 'en'); }
      catch (_) { format = new Intl.NumberFormat('en'); }
      const updates = [
        [summary.querySelector('[data-cs-displayed-count]'), format.format(displayed)],
        [summary.querySelector('[data-cs-total-count]'), format.format(total)]
      ];
      updates.forEach(([element, value]) => {
        // Avoid generating a MutationObserver loop on our own count text.
        if (element && element.textContent !== value) element.textContent = value;
      });
    }
  }
  customElements.define(tag, CoralCollectionView);
})();
